import React, { Fragment } from 'react';
import { translate } from "../Services/translation";

const QuickSearchKeys = Object.freeze({
    Products: "Products",
    Categories: "Categories",
    Pages: "Pages",
    Brands: "Brands",
    ShowAll: "ShowAll",
    NoHit: "NoHit"
});

/**
 * @type {SearchResultItem}
 */
const noHitItem = {
    categoryKey: QuickSearchKeys.NoHit,
    categoryLabel: QuickSearchKeys.NoHit,
    name: translate('nohit'),
    url: null,
    imageSource: null,
    hasImage: false,
    showAll: false
}

const getNoHitItem = (key) => ({
    ...noHitItem,
    categoryKey: key
});

/**
 * @typedef {Object} SearchResultItem
 * @property {string} categoryKey - The category key, this value will be constant.
 * @property {string} categoryLabel - The category label, will change based on language.
 * @property {string} name - The name of the search result item.
 * @property {string|null} url - The URL path of the search result item.
 * @property {string|null} imageSource - The source URL of the image, or `null` if there is no image.
 * @property {boolean} hasImage - Indicates whether the item has an image.
 * @property {boolean} showAll - Indicates whether to show all results for this item.
 */

/**
 * @param {SearchResultItem} item
 * @returns {JSX.Element}
 * @constructor
 */
const QuickSearchResultItem = ({item}) => {
    let type = item.categoryKey;
    let typeWithImage = type === QuickSearchKeys.Products;
    let hasUrl = item.url !== null && item.url;

    return (
        <div className={`quick-search__result-item quick-search__${type.toLowerCase()}-item`}>
            {typeWithImage && item.hasImage && (
                <div className="quick-search__result-image">
                    {item.hasImage && (<img src={item.imageSource} alt={item.name} />)}
                </div>
            )}

            {hasUrl ? (
                <a href={item.url} className="quick-search__result-link" title={item.name}>{item.name}</a>
            ) : (
                <span className="quick-search__result-link">{item.name}</span>
            )}
        </div>
    );
}

/**
 *
 * @param {SearchResultItem[]} result
 * @param {string} type
 * @returns {JSX.Element}
 * @constructor
 */
const SearchCategoryResult = ({ result, type, showMore, searchUrl }) => {
    let key = result[0].categoryKey.toLowerCase();

    return (
        <div className={`quick-search__category-result quick-search__${type}-result`}>
            {result && result.length > 0 && (
                <h4>{translate(key)}</h4>
            )}

            {result && result.length > 0 && result.map((item, index) => (
                <QuickSearchResultItem item={item} key={item.categoryKey + index}/>
            ))}

            {result && result.length > 0 && showMore && (
                <a href={searchUrl} className="quick-search__show-all-results">
                    <span>{translate('general.search.showallresults')}</span>
                </a>
            )}
        </div>
    );
}

/**
 *
 * @param {SearchResultItem[]} result
 * @param selectedItem
 * @param {string} searchUrl
 * @returns {JSX.Element}
 * @constructor
 */
const QuickSearchResult = ({result, selectedItem, searchUrl}) => {
    let allProductItems = result ? result.filter(r => r.categoryKey === QuickSearchKeys.Products) : [];
    let visibleProductItems = allProductItems.slice(0, 5);
    if (!allProductItems.length) {
        visibleProductItems.push(getNoHitItem(QuickSearchKeys.Products));
    }

    let categoryItems = result ? result.filter(r => r.categoryKey === QuickSearchKeys.Categories).slice(0, 5) : [];
    if (!categoryItems.length) {
        categoryItems.push(getNoHitItem(QuickSearchKeys.Categories));
    }

    let pageItems = result ? result.filter(r => r.categoryKey === QuickSearchKeys.Pages).slice(0, 5) : [];
    if (!pageItems.length) {
        pageItems.push(getNoHitItem(QuickSearchKeys.Pages));
    }

    return (
        <ul className="quick-search__result-container">
            <ul className="quick-search__result">
                <SearchCategoryResult result={visibleProductItems} showMore={allProductItems.length > 5} searchUrl={searchUrl}/>
                <SearchCategoryResult result={pageItems} />
                <SearchCategoryResult result={categoryItems} />
            </ul>
        </ul>
    )
}

export default QuickSearchResult
